import { Badge, Button, Card, Col, Row, Skeleton, Space, TreeSelect } from "antd";
import { useLocation, useOutletContext, useParams } from "react-router";
import { IAppDetails, PodPhase } from "../../types";
import { CodeSandboxOutlined, GlobalOutlined } from '@ant-design/icons';
import { Content } from "antd/lib/layout/layout";
import { useEffect, useRef, useState } from "react";
import { getKubeService } from "../../backend";
import { Link } from "react-router-dom";
import Logs from "../../components/Logs";
import { GetMicroservicePodEventsResponseEvents } from "@microtica/ms-kube-sdk";
import moment from "moment-timezone";
import GitCommitLink from "../../components/GitCommitLink";
import CircleProgress from "../../components/CircleProgress";
import AssignAppDomainModal from "./AssignAppDomainModal";
import { trackAssignDomainInit } from "../../backend/tracking/services";
import KubernetesIcon from "../../assets/kubernetes-icon.svg";
import GitIcon from "../../assets/git-icon.svg";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import MessageCard from "../../components/MessageCard";

const AppMonitoring = () => {
    const { projectId, envId, appName } = useParams();
    const { state } = useLocation();
    const { appDetails } = useOutletContext<{ appDetails: IAppDetails }>();
    const [logs, setLogs] = useState<string>("");
    const [events, setEvents] = useState<GetMicroservicePodEventsResponseEvents[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedContainer, setSelectedContainer] = useState(
        `${appDetails.monitoring.pods[0]?.name}:${appDetails.monitoring.pods[0]?.containers[0]?.name}`
    );
    const [logInterval, setLogInterval] = useState<NodeJS.Timer>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const logTimestampColor = "#097BB5";
    const logsRef = useRef(null);
    const localTimezoneOffset = moment().utcOffset();
    const [executeScroll, setExecuteScroll] = useState<boolean>();
    const [appPods, setAppPods] = useState<any>(appDetails.monitoring.pods);
    const currentProject = useRecoilValue(currentProjectState);
    const [domainDisplaySettings, setDomainDisplaySettings] = useState<{ ctaCard: boolean, assignCustomDomainBtn: boolean, displayDomain: boolean }>();

    const loadData = async (podName: string, containerName: string) => {
        if (!appDetails.monitoring.pods || !appDetails.monitoring.pods.length) {
            setLoading(false);
            return setLogs(`[Microtica] There are no available running pods for this application \n
                [Microtica] Either the app does not require pods or you don't have enough resources available on the cluster to run the pods`);
        }
        const logs = await fetchLogs(podName, containerName);
        const containerPhase = appDetails.monitoring.pods.find(container => container.name === podName)?.phase;
        if (!logs || containerPhase?.toLowerCase() === "pending") {
            // Display the events if logs do not exist OR the container is Pending
            const events = await fetchEvents(podName);
            setLogs("");
            setEvents(events);
        } else {
            // Display the actual logs only if logs exist AND the container is not in a Pending state
            setLogs(logs);
        }
        setLoading(false);
    }

    const setupLogRefresh = async (podName: string, containerName: string) => {
        setLoading(true);
        await loadData(podName, containerName);
        setLoading(false);
        // trigger scroll to last log
        setExecuteScroll(true);

        // Remove old interval if any
        logInterval && clearInterval(logInterval);
        const interval = setInterval(async () => {
            await loadData(podName, containerName);
        }, 5000);
        setLogInterval(interval);
        return interval;
    }

    useEffect(() => {
        return () => {
            logInterval && clearInterval(logInterval);
        }
    }, [logInterval]);

    useEffect(() => {
        const [podName, containerName] = selectedContainer.split(":");
        setupLogRefresh(podName, containerName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContainer]);

    useEffect(() => {
        // initiate scroll to last log if the scroll has not been executed
        // scrollExecuted is set to false the first time logs for a container are fetched
        if (executeScroll) {
            scrollToLastLog();
            // do not scroll to last log anymore
            setExecuteScroll(false);
        }
    }, [executeScroll]);

    useEffect(() => {
        if (!!(state as any)?.scrollToLogs) {
            (logsRef as any).current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [state]);

    const fetchLogs = async (podName: string, containerName: string) => {
        let { data: { logs } } = await getKubeService().getMicroserviceContainerLogs(
            appName!,
            appDetails.namespace,
            appDetails.clusterId,
            podName,
            containerName,
            projectId!
        );
        return logs;
    }

    const fetchEvents = async (podName: string) => {
        const { data: { events } } = await getKubeService()
            .getMicroservicePodEvents(appName!, appDetails.namespace, appDetails.clusterId, podName, projectId!);
        return events;
    }

    const formatLogTypeFontColor = (text: string) => {
        switch (text) {
            case "warning":
                return "var(--warning-color)";
            case "error":
                return "var(--error-color)";
            default:
                return "white";
        }
    }

    const scrollToLastLog = () => {
        const lastChild = document?.getElementById("logs-container")?.lastElementChild;
        lastChild?.scrollIntoView({ behavior: "smooth", block: "end" });
    }

    const PodPhaseIcon = (props: { phase: PodPhase }) => {
        switch (props.phase) {
            case "Pending":
                return <Badge color="blue" />
            case "Running":
                return <Badge color="green" />
            case "Succeeded":
                return <Badge color="green" />
            case "Failed":
                return <Badge color="red" />
            default:
                return <Badge color="black" />
        }
    }

    useEffect(() => {
        const interval = setInterval(async () => {
            await getDeploymentStatus();
        }, 10000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getDeploymentStatus = async () => {
        const { data: deploymentStatus } = await getKubeService().getMicroserviceDeploymentStatus(
            appName!,
            appDetails.clusterId,
            appDetails.namespace,
            projectId!
        );
        const pods = (deploymentStatus.pods).map(p => ({
            name: p.metadata.name,
            phase: p.status.phase as PodPhase,
            containers: p.status.containerStatuses?.map(container => ({
                name: container.name,
                image: container.image,
                restartCount: container.restartCount,
                ready: container.ready
            })) || []
        }));
        setAppPods(pods);
    }

    useEffect(() => {        /**
    * free & no domain                     - MessageCard
    * free & domain (microtica.rocks)      - MessageCard - display domain
    * free & domain (custom)               - NOT POSSIBLE
    * starter & no domain                  - Button
    * starter & domain (microtica.rocks)   - display domain
    * starter & domain (custom)            - display domain
    */
        if (currentProject.paymentPlan?.id === "FREE") {
            // Free plan project
            if (appDetails.isDomainWorking) {
                // Active domain
                if (appDetails.domain.endsWith("microtica.rocks")) {
                    // Free plan & Active Microtica domain
                    setDomainDisplaySettings({
                        ctaCard: true,
                        assignCustomDomainBtn: false,
                        displayDomain: true
                    });
                }
            } else {
                // Free plan & No domain
                setDomainDisplaySettings({
                    ctaCard: true,
                    assignCustomDomainBtn: false,
                    displayDomain: false
                });
            }
        } else {
            // Paid plan project
            if (appDetails.isDomainWorking) {
                // Paid plan & any domain
                setDomainDisplaySettings({
                    ctaCard: false,
                    assignCustomDomainBtn: false,
                    displayDomain: true
                });
            } else {
                // Paid plan & No domain
                setDomainDisplaySettings({
                    ctaCard: false,
                    assignCustomDomainBtn: true,
                    displayDomain: false
                });
            }
        }
    }, [currentProject, appDetails])

    return (
        <Space direction="vertical" size="middle" className="full-width">
            <Card bordered title="Details">
                {
                    domainDisplaySettings?.ctaCard &&
                    <Row gutter={24} style={{ marginBottom: 20 }}>
                        <Col xs={24}>
                            <MessageCard
                                type="cta"
                                icon={
                                    <GlobalOutlined style={{ fontSize: 26, color: "#3399ff" }} />
                                }
                                text={
                                    <div>
                                        <div style={{ fontWeight: 600 }}>Custom domain</div>
                                        <div>
                                            Set a custom domain to enhance your app's appeal and improve your customers' experience.
                                        </div>
                                    </div>
                                }
                                extra={
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            setIsModalVisible(true);
                                            trackAssignDomainInit({
                                                serviceName: appDetails.name,
                                                clusterName: appDetails.clusterId
                                            });
                                        }}
                                    >
                                        Assign custom domain
                                    </Button>
                                }
                            />
                        </Col>
                    </Row>
                }
                <Row gutter={24}>
                    <Col xs={24} md={14} style={{ display: "grid" }}>
                        <Card>
                            {/* Cluster details */}
                            <Row gutter={24} align="middle">
                                <Col xs={2}>
                                    <img src={KubernetesIcon} style={{ height: "30px" }} alt="kubernetes" />
                                </Col>
                                <Col xs={22}>
                                    <Row gutter={24}>
                                        <Col xs={14}>
                                            <div>
                                                Name
                                            </div>
                                            <div><b>{appDetails.clusterId}</b></div>
                                        </Col>
                                        <Col xs={10}>
                                            <div>Namespace</div>
                                            <div><b>{appDetails.namespace}</b></div>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            <br />

                            {/* Git commit info */}
                            <Row gutter={24} align="middle">
                                <Col xs={2}>
                                    <img src={GitIcon} style={{ height: "30px" }} alt="git" />
                                </Col>
                                <Col xs={22}>
                                    <Space direction="vertical" size={0}>
                                        <b>{appDetails.commitMessage}</b>
                                        {appDetails.repositoryUrl}
                                        <div>
                                            <GitCommitLink version={appDetails.version} repoUrl={appDetails.repositoryUrl} branch={appDetails.branch} /> {appDetails.commitDate ? moment(appDetails.commitDate).fromNow() : null}
                                        </div>
                                    </Space>
                                </Col>
                            </Row>

                            <br />

                            {/* Domain info */}
                            {
                                (
                                    domainDisplaySettings?.assignCustomDomainBtn &&
                                    <Row gutter={24}>
                                        <Col offset={2} xs={22}>
                                            <Button
                                                onClick={() => {
                                                    setIsModalVisible(true);
                                                    trackAssignDomainInit({
                                                        serviceName: appDetails.name,
                                                        clusterName: appDetails.clusterId
                                                    });
                                                }}
                                                type="primary">
                                                <GlobalOutlined />
                                                Assign domain
                                            </Button>
                                        </Col>
                                    </Row>
                                )
                            }
                            {
                                domainDisplaySettings?.displayDomain &&
                                <Row gutter={24} align="middle">
                                    <Col xs={2}>
                                        <GlobalOutlined style={{ fontSize: 25 }} />
                                    </Col>
                                    <Col xs={22}>
                                        <div>Domain</div>
                                        <a href={`https://${appDetails.domain}`} target="_blank" rel="noreferrer">
                                            {appDetails.domain}
                                        </a>
                                    </Col>
                                </Row>
                            }
                        </Card>
                    </Col>
                    <CircleProgress
                        title="Memory"
                        subtitle={
                            <>
                                <div>Current Memory Usage</div>
                                <div>(MiB)</div>
                            </>
                        }
                        percent={
                            appDetails.monitoring && appDetails.monitoring.memoryUsage !== "NaNMi" ?
                                parseInt(appDetails.monitoring.memoryUsage) / appDetails.monitoring.memoryLimit * 100 :
                                0
                        }
                        mdSize={5}
                        currentUsage={appDetails.monitoring.memoryUsage !== "NaNMi" ? parseInt(appDetails.monitoring.memoryUsage) : undefined}
                        limit={appDetails.monitoring.memoryLimit}
                    />
                    <CircleProgress
                        title="CPU"
                        subtitle={
                            <>
                                <div>Current CPU Usage</div>
                                <div>(m)</div>
                            </>
                        }
                        percent={
                            appDetails.monitoring && appDetails.monitoring.cpuUsage !== "NaNm" ?
                                parseInt(appDetails.monitoring.cpuUsage) / appDetails.monitoring.cpuLimit * 100 :
                                0
                        }
                        mdSize={5}
                        currentUsage={appDetails.monitoring.cpuUsage !== "NaNm" ? parseInt(appDetails.monitoring.cpuUsage) : undefined}
                        limit={appDetails.monitoring.cpuLimit}
                    />
                </Row>
                <Row gutter={24}>
                    <Col xs={24}>
                        <small>
                            Limit the resource usage and configure the number of instances in the <Link to={`/projects/${projectId}/environments/${envId}/apps/${appDetails?.clusterId}/${appDetails?.namespace}/${appName}/settings/scaling`}>Scaling</Link> settings.
                        </small>
                    </Col>
                </Row>
            </Card>
            <br />
            <div ref={logsRef}>
                <Card
                    bordered
                    title={
                        <div style={{ position: "relative", display: "inline-block" }}>
                            <div className="pulsating-circle"></div>
                            <div style={{ marginLeft: 35 }}>
                                Realtime logs
                            </div>
                        </div>
                    }
                    extra={
                        <Space>
                            Containers:
                            <TreeSelect
                                style={{ width: '400px' }}
                                defaultValue={selectedContainer || "No available containers"}
                                value={selectedContainer}
                                onChange={setSelectedContainer}
                                placeholder="Please select container"
                                treeDefaultExpandAll
                                multiple={false}
                                treeData={
                                    appPods.map((pod: any) => ({
                                        value: pod.name,
                                        title: <div style={{ color: "GrayText" }}>
                                            <PodPhaseIcon phase={pod.phase} /> {pod.name} ({pod.phase})
                                        </div>,
                                        selectable: false,
                                        disableCheckbox: true,
                                        children: pod.containers.map((c: { name: string, restartCount: number }) => ({
                                            value: `${pod.name}:${c.name}`,
                                            title: <div className="flex-justify-space-between">
                                                <div>
                                                    <CodeSandboxOutlined /> {c.name}
                                                </div>
                                                <small style={{ color: "GrayText" }}>
                                                    {c.restartCount} restarts
                                                </small>
                                            </div>
                                        }))
                                    }))
                                }
                            />
                        </Space>
                    }
                >
                    <Content className="content-logs" style={{ overflowY: "scroll", maxHeight: 500 }}>
                        <Logs title="Container Logs">
                            {!loading ?
                                logs ?
                                    <div id="logs-container">
                                        {logs.split("\n").map((log, index) => {
                                            const timestamp: string = log.split(" ")[0];
                                            // color formatting if the log has a valid timestamp
                                            return moment(timestamp).isValid() ?
                                                <div key={index}>
                                                    <span style={{ color: logTimestampColor }}>
                                                        {/* display the timestamp */}
                                                        [{
                                                            moment(timestamp).utc()
                                                                .add(localTimezoneOffset, 'm')
                                                                .format("YYYY-MM-DD HH:mm:ss.SSS")
                                                        }]
                                                    </span>&nbsp;
                                                    <span>
                                                        {/* display the rest of the log */}
                                                        {log.split(" ").slice(1).join(" ")}
                                                    </span>
                                                </div>
                                                :
                                                <div key={index}>
                                                    {log}
                                                </div>
                                        })}
                                    </div>
                                    :
                                    events && events.length ?
                                        events.map((event, index) => {
                                            return <div key={index}>
                                                <span style={{ color: logTimestampColor }}>
                                                    {event.firstSeen ? moment(event.firstSeen).format() : "-------------------------"}
                                                </span>&nbsp;
                                                <span style={{ color: formatLogTypeFontColor(event.type!.toLowerCase()) }}>
                                                    [{event.type?.toUpperCase()}]
                                                </span>&nbsp;
                                                <span>{event.message}</span>&nbsp;
                                                {event.count! > 1 &&
                                                    <span>
                                                        (x{event.count} over {moment.duration(event.lastSeen! - event.firstSeen!, "milliseconds").humanize()})
                                                    </span>
                                                }
                                            </div>
                                        }) :
                                        <>[Microtica] There are currently no log messages to be displayed for this instance</> :
                                <Skeleton />
                            }
                        </Logs>
                    </Content>
                </Card>
            </div>
            {
                isModalVisible &&
                <AssignAppDomainModal
                    visible={isModalVisible}
                    appDetails={appDetails}
                    envId={envId!}
                    onCancel={() => setIsModalVisible(false)}
                />
            }
        </Space>
    )
}

export default AppMonitoring;